import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SitePresentationWeb.scss';

interface FileTab {
    id: string;
    label: string;
    language: string;
    code: string;
    typingSpeed: number;
    pauseAfter: number;
}

interface Tab {
    id: string;
    label: string;
    language: string;
    fullCode: string;
    lines: string[];
    charCount: number;
    currentLineIndex: number;
    isComplete: boolean;
    typingSpeed: number;
    pauseAfter: number;
    _typed: string;
    _initialLines: string[];
}

const SitePresentationWeb: React.FC = () => {
    const FILES: FileTab[] = [
        {
            id: 'tsx',
            label: 'App.tsx',
            language: 'typescript',
            code: `import React, { useState } from 'react';
import './App.scss';

const App: React.FC<AppProps> = ({ title }) => {
    const [count, setCount] = useState<number>(0);

    const handleClick = () => {
        setCount(count + 1);
    };

    return (
        <div className="app">
            <h1>{title}</h1>
            <p>Contatore: {count}</p>
            <button onClick={handleClick}>
                Incrementa
            </button>
        </div>
    );
};

export default App;`,
            typingSpeed: 45,
            pauseAfter: 1200
        },
        {
            id: 'scss',
            label: 'App.scss',
            language: 'scss',
            code: `.app {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: #282c34;
    color: white;
    font-family: 'Segoe UI', sans-serif;

    h1 {
        font-size: 3rem;
        margin-bottom: 1rem;
        color: #61dafb;
    }

    button {
        padding: 0.8rem 2rem;
        font-size: 1.2rem;
        border: none;
        border-radius: 8px;
        background: #61dafb;
        color: #282c34;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
            background: #4fc3f7;
        }
    }
}`,
            typingSpeed: 50,
            pauseAfter: 800
        }
    ];

    const TERMINAL_COMMAND = 'npm start';
    const TERMINAL_OUTPUT = `> my-app@0.1.0 start
> react-scripts start

Compiled successfully!

You can now view my-app in the browser.

    Local:            http://localhost:3000
    On Your Network:  http://192.168.1.100:3000

Note that the development build is not optimized.
To create a production build, use npm run build.

webpack compiled successfully`;

    const [tabs, setTabs] = useState<Record<string, Tab>>({});
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [stopRequested, setStopRequested] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isPausing, setIsPausing] = useState(false);
    const [terminalOpened, setTerminalOpened] = useState(false);
    const [terminalLines, setTerminalLines] = useState<string[]>([]);
    const [statusDot, setStatusDot] = useState<'idle' | 'typing' | 'done' | 'terminal'>('idle');
    const [statusText, setStatusText] = useState('Pronto');
    const [statusLang, setStatusLang] = useState('TSX');
    const [statusLine, setStatusLine] = useState('Linea 1');
    const [statusChars, setStatusChars] = useState('0 caratteri');

    const codeAreaRef = useRef<HTMLDivElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const terminalContentRef = useRef<HTMLDivElement>(null);
    const isRunningRef = useRef(false);
    const stopRequestedRef = useRef(false);

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // ---------- HIGHLIGHTING ----------
    const highlightTSX = (line: string): React.ReactNode => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        // Regex migliorata per TSX: cattura tag JSX (nome + attributi) e attributi specifici
        const regex = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\/\/[^\r\n]*|\/\*[\s\S]*?\*\/|\b(?:const|let|var|function|return|import|export|class|interface|type|async|await|useState|useEffect|React|FC)\b|\b(?:className|onClick|onChange|onSubmit|onKeyDown|onBlur|onFocus|onMouseEnter|onMouseLeave|style|id|key|ref|children|dangerouslySetInnerHTML)\b|<\/?[A-Za-z][\w:.-]*(?:\s[^<>]*?)?\/?>|=>|[{}[\]();,.:&]|#[A-Za-z_][\w-]*|[A-Za-z_$][A-Za-z0-9_$]*)/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
                parts.push(line.substring(lastIndex, match.index));
            }

            const token = match[0];
            let className = '';

            // Riconoscimento prioritario
            if (token.match(/^["'`]/)) {
                className = 'syn-str';
            } else if (token.match(/^\/\//) || token.match(/^\/\*/)) {
                className = 'syn-comment';
            } else if (token.match(/^(const|let|var|function|return|import|export|class|interface|type|async|await)\b/)) {
                className = 'syn-keyword';
            } else if (token.match(/^(useState|useEffect|React|FC)\b/)) {
                className = 'syn-builtin';
            } else if (token.match(/^(className|onClick|onChange|onSubmit|onKeyDown|onBlur|onFocus|onMouseEnter|onMouseLeave|style|id|key|ref|children|dangerouslySetInnerHTML)\b/)) {
                className = 'syn-attr';
            } else if (token.match(/^\d/)) {
                className = 'syn-num';
            } else if (token.match(/^</)) {  // Tag JSX
                className = 'syn-tag';
            } else if (token === '{' || token === '}') {
                className = 'syn-jsx-brace';
            } else if (token.match(/^=>/)) {
                className = 'syn-keyword';
            } else if (token === '.' || token === '?' || token === '?.') {
                className = 'syn-method';
            } else if (token === '#') {
                className = 'syn-prop';
            } else if (token.match(/^[a-zA-Z_$]/)) {
                if (token.match(/^(true|false|null|undefined)\b/)) {
                    className = 'syn-num';
                } else {
                    className = 'syn-var';
                }
            }

            parts.push(
                className ? <span key={lastIndex} className={className}>{token}</span> : token
            );
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < line.length) {
            parts.push(line.substring(lastIndex));
        }

        return <>{parts}</>;
    };

    const highlightSCSS = (line: string): React.ReactNode => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        const regex = /(\/\/.*|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|#[a-zA-Z_][\w-]*|\.[a-zA-Z_][\w-]*|&|::?[a-zA-Z_][\w-]*|@[a-zA-Z_][\w-]*|\b\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|s|ms)?\b|\b(true|false|null)\b|[\w-]+)/g;
        let match;

        // Set di proprietà CSS (ridotto per brevità)
        const cssProps = new Set([
            'display', 'flex-direction', 'align-items', 'justify-content', 'min-height',
            'background', 'color', 'font-family', 'font-size', 'margin-bottom', 'padding',
            'border', 'border-radius', 'cursor', 'transition', '&:hover'
        ]);

        // Valori comuni
        const cssValues = new Set([
            'flex', 'column', 'center', 'space-between', 'space-around', 'stretch',
            'auto', 'inherit', 'initial', 'unset', 'none', 'block', 'inline', 'inline-block',
            'inline-flex', 'relative', 'absolute', 'fixed', 'static', 'sticky',
            'visible', 'hidden', 'scroll', 'cover', 'contain', 'fill',
            'white', '#282c34', '#61dafb', '#4fc3f7', '1rem', '1.2rem', '1.5rem', '3rem',
            '100vh', '0.8rem', '2rem', '8px', '0.2s'
        ]);

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
                parts.push(line.substring(lastIndex, match.index));
            }

            const token = match[0];
            let className = '';

            if (token.match(/^\/\//) || token.match(/^\/\*/)) {
                className = 'syn-comment';
            } else if (token.match(/^["']/)) {
                className = 'syn-str';
            } else if (token.match(/^\d/)) {
                className = 'syn-num';
            } else if (token.match(/^(true|false|null)\b/)) {
                className = 'syn-num';
            } else if (token.match(/^#[a-zA-Z_]/) || token.match(/^\.[a-zA-Z_]/) || token.match(/^&/) || token.match(/^::?[a-zA-Z_]/)) {
                className = 'syn-selector';
            } else if (token.match(/^@/)) {
                className = 'syn-keyword';
            } else if (cssProps.has(token)) {
                className = 'syn-property';
            } else if (cssValues.has(token)) {
                className = 'syn-value';
            } else {
                className = 'syn-var';
            }

            parts.push(
                className ? <span key={lastIndex} className={className}>{token}</span> : token
            );
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < line.length) {
            parts.push(line.substring(lastIndex));
        }

        return <>{parts}</>;
    };

    const highlightLine = (line: string, language: string): React.ReactNode => {
        if (language === 'scss') {
            return highlightSCSS(line);
        } else {
            return highlightTSX(line);
        }
    };

    // ---------- INIT ----------
    const initTabs = useCallback(() => {
        const newTabs: Record<string, Tab> = {};
        FILES.forEach((file) => {
            newTabs[file.id] = {
                id: file.id,
                label: file.label,
                language: file.language,
                fullCode: file.code,
                lines: [''],           // inizia con una riga vuota
                charCount: 0,
                currentLineIndex: 0,
                isComplete: false,
                typingSpeed: file.typingSpeed || 45,
                pauseAfter: file.pauseAfter || 800,
                _typed: '',
                _initialLines: file.code.split('\n'),
            };
        });
        const firstId = FILES[0].id;
        setActiveTabId(firstId);
        setTabs(newTabs);
        setStatusDot('idle');
        setStatusText('Pronto');
    }, []);

    const updateTab = (tabId: string, updates: Partial<Tab>) => {
        setTabs(prev => ({
            ...prev,
            [tabId]: { ...prev[tabId], ...updates }
        }));
    };

    // ---------- TYPING ----------
    const typeFile = useCallback(async (fileId: string, fileLabel: string, fullText: string, typingSpeed: number, pauseAfter: number, lang: string) => {
        // Resetta completamente il tab a vuoto
        updateTab(fileId, {
            lines: [''],
            charCount: 0,
            currentLineIndex: 0,
            isComplete: false,
            _typed: ''
        });

        setIsTyping(true);
        setIsPausing(false);
        setActiveTabId(fileId);
        setStatusDot('typing');
        setStatusText(`${fileLabel}`);
        setStatusLang(lang.charAt(0).toUpperCase() + lang.slice(1));

        let typed = '';
        for (let i = 0; i < fullText.length; i++) {
            if (stopRequestedRef.current) {
                setIsTyping(false);
                return;
            }

            const ch = fullText[i];
            typed += ch;
            const newLines = typed.split('\n');
            const currentLineIndex = newLines.length - 1;

            updateTab(fileId, {
                _typed: typed,
                lines: newLines,
                charCount: typed.length,
                currentLineIndex: currentLineIndex
            });

            setStatusLine(`Linea ${currentLineIndex + 1}`);
            setStatusChars(`${typed.length} caratteri`);

            if (codeAreaRef.current) {
                codeAreaRef.current.scrollTop = codeAreaRef.current.scrollHeight;
            }

            await sleep(typingSpeed);
        }

        updateTab(fileId, { isComplete: true });
        setIsTyping(false);
        setStatusDot('done');
        setStatusText(`${fileLabel} completato`);

        setIsPausing(true);
        await sleep(pauseAfter);
        setIsPausing(false);
    }, []);

    // ---------- TERMINAL ----------
    const typeTerminalOutput = useCallback(async () => {
        setTerminalOpened(true);
        setStatusDot('terminal');
        setStatusText('Esecuzione npm start…');
        setTerminalLines([`$ ${TERMINAL_COMMAND}`]);

        await sleep(600);

        const outputLines = TERMINAL_OUTPUT.split('\n');
        const allLines = [`$ ${TERMINAL_COMMAND}`];

        for (const line of outputLines) {
            allLines.push(line);
            setTerminalLines([...allLines]);
            await sleep(80);
            if (terminalContentRef.current) {
                terminalContentRef.current.scrollTop = terminalContentRef.current.scrollHeight;
            }
        }

        await sleep(400);
        setStatusDot('done');
        setStatusText('✅ Progetto avviato');
    }, []);

    // ---------- START / RESTART ----------
    const startPresentation = useCallback(async () => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;
        stopRequestedRef.current = false;
        setIsRunning(true);
        setStopRequested(false);
        setTerminalOpened(false);
        setTerminalLines([]);

        initTabs();
        await sleep(300);

        for (let i = 0; i < FILES.length; i++) {
            if (stopRequestedRef.current) break;
            const file = FILES[i];
            if (i > 0) {
                setActiveTabId(file.id);
                await sleep(300);
            }
            await typeFile(file.id, file.label, file.code, file.typingSpeed, file.pauseAfter, file.language);
            if (stopRequestedRef.current) break;
        }

        if (!stopRequestedRef.current) {
            await sleep(400);
            await typeTerminalOutput();
        }

        isRunningRef.current = false;
        setIsRunning(false);
    }, [initTabs, typeFile, typeTerminalOutput, FILES]);

    const restartPresentation = useCallback(() => {
        stopRequestedRef.current = true;
        setTimeout(() => {
            setTerminalOpened(false);
            setTerminalLines([]);
            startPresentation();
        }, 300);
    }, [startPresentation]);

    useEffect(() => {
        const timer = setTimeout(() => {
            startPresentation();
        }, 600);
        return () => clearTimeout(timer);
    }, [startPresentation]);

    const currentTab = activeTabId ? tabs[activeTabId] : null;

    return (
        <div className="idea-window">
            {/* TITLE BAR */}
            <div className="title-bar" onClick={restartPresentation}>
                <div className="title-bar-left">
                    <div className="window-controls">
                        <span className="win-close"></span>
                        <span className="win-minimize"></span>
                        <span className="win-maximize"></span>
                    </div>
                    <div className="title-text">
                        <span>WebStorm</span>
                    </div>
                </div>
                <div className="title-bar-right">
                    <span>WebSite</span>
                </div>
            </div>

            {/* TAB BAR */}
            <div className="tab-bar">
                {Object.values(tabs).map(tab => (
                    <div
                        key={tab.id}
                        className={`tab ${activeTabId === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTabId(tab.id)}
                    >
                        <span>{tab.label}</span>
                    </div>
                ))}
            </div>

            {/* EDITOR */}
            <div className="editor-container">
                <div className="line-numbers" ref={lineNumbersRef}>
                    {currentTab?.lines.map((_, i) => (
                        <div key={i} className="ln">{i + 1}</div>
                    ))}
                </div>
                <div className="code-area" ref={codeAreaRef}>
                    <div className="code-lines">
                        {currentTab?.lines.map((line, idx) => (
                            <div key={idx} className="code-line">
                                {highlightLine(line, currentTab.language)}
                                {idx === currentTab.currentLineIndex && (isTyping || isPausing) && <span className="cursor-blink"></span>}
                            </div>
                        ))}
                    </div>
                    {currentTab && (
                        <div className="lang-badge-wrapper">
                            <div className={`lang-badge ${currentTab.language}`}>
                                {currentTab.language === 'typescript' ? 'TSX' : 'SCSS'}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* TERMINAL */}
            {terminalOpened && (
                <div className="terminal-panel open">
                    <div className="terminal-content" ref={terminalContentRef}>
                        {terminalLines.map((line, idx) => (
                            <div key={idx} className={`terminal-line ${line.startsWith('$') ? 'terminal-prompt' : ''}`}>
                                {line}
                            </div>
                        ))}
                        {statusDot === 'terminal' && <span className="terminal-cursor"></span>}
                    </div>
                </div>
            )}

            {/* STATUS BAR */}
            <div className="status-bar" onClick={restartPresentation}>
                <div className="status-left">
                    <span className={`status-dot ${statusDot}`}></span>
                    <span>{statusText}</span>
                    <span>{statusLang}</span>
                    <span>{statusLine}</span>
                </div>
                <div className="status-right">
                    <span>UTF-8</span>
                    <span>LF</span>
                    <span>{statusChars}</span>
                </div>
            </div>
        </div>
    );
};

export default SitePresentationWeb;